import { useCallback, useState } from 'react';
import { ArrowLeftRight, Ban, FolderPlus, Plus, Trash2, X } from 'lucide-react';
import { useEditor } from '../../state/store';
import { Color, TRANSPARENT, colorToHex, hexToColor } from '../../core/types';
import { BUILTIN_PALETTES } from '../../ansi/Palette';
import {
  SavedPalette,
  addColorToPalette,
  createPalette,
  listPalettes,
  removeColorFromPalette,
  removePalette,
} from '../../state/library';
import { Field, Hint, Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';
import { ColorPicker } from '../widgets/ColorPicker';

/**
 * Colour for whatever gets painted next — characters, text, shapes, fills.
 *
 * Foreground and background are edited with the same picker; the pair swatch
 * shows which one is active and swaps them on click, the way paint programs do.
 */
export function ColorPanel(): JSX.Element {
  const brush = useEditor((s) => s.brush);
  const setBrush = useEditor((s) => s.setBrush);
  const setTool = useEditor((s) => s.setTool);
  const recent = useEditor((s) => s.recentColors);
  const [target, setTarget] = useState<'fg' | 'bg'>('fg');
  const [paletteName, setPaletteName] = useState(BUILTIN_PALETTES[1].name);

  const current = target === 'fg' ? brush.fg : brush.bg;
  const apply = (color: Color) => setBrush(target === 'fg' ? { fg: color } : { bg: color });
  const palette = BUILTIN_PALETTES.find((p) => p.name === paletteName) ?? BUILTIN_PALETTES[0];

  return (
    <Panel title="Colour" collapsible={false}>
      <div className="row" style={{ alignItems: 'stretch', gap: 10, marginBottom: 10 }}>
        <div
          className="color-preview"
          title="Swap text and background (Shift+X)"
          onClick={() => setBrush({ fg: brush.bg, bg: brush.fg })}
        >
          <span className="fg" style={{ background: colorToHex(brush.fg) }} />
          <span className="bg" style={{ background: colorToHex(brush.bg) }} />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="segmented">
            <button className={target === 'fg' ? 'active' : ''} onClick={() => setTarget('fg')}>
              Text
            </button>
            <button className={target === 'bg' ? 'active' : ''} onClick={() => setTarget('bg')}>
              Background
            </button>
          </div>
          <div className="row">
            <button
              className="btn solid"
              style={{ flex: 1 }}
              onClick={() => setBrush({ fg: brush.bg, bg: brush.fg })}
              title="Swap text and background colours"
            >
              <ArrowLeftRight size={14} /> Swap
            </button>
            <button
              className={`btn icon${current === TRANSPARENT ? ' active' : ''}`}
              title="Use the terminal's own default colour"
              onClick={() => apply(TRANSPARENT)}
            >
              <Ban size={14} />
            </button>
          </div>
        </div>
      </div>

      <ColorPicker
        value={current === TRANSPARENT ? 0 : current}
        onChange={apply}
        onPickFromCanvas={() => setTool('eyedropper')}
      />

      {current === TRANSPARENT ? (
        <Hint>
          Currently set to the terminal default — the terminal decides this colour. Pick a value
          above to override it.
        </Hint>
      ) : null}

      <Field label="Palette">
        <Select
          ariaLabel="Palette"
          value={paletteName}
          options={BUILTIN_PALETTES.map((p) => ({ value: p.name, label: p.name }))}
          onChange={setPaletteName}
        />
      </Field>

      <div className="swatch-grid" style={{ marginBottom: 10 }}>
        {palette.colors.map((hex) => (
          <button
            key={hex}
            className={`swatch${hexToColor(hex) === current ? ' selected' : ''}`}
            style={{ background: hex }}
            title={hex}
            onClick={() => apply(hexToColor(hex))}
          />
        ))}
      </div>

      <div className="group-title">Recent</div>
      <div className="swatch-grid" style={{ marginBottom: 10 }}>
        {recent.map((c) => (
          <button
            key={c}
            className={`swatch${c === current ? ' selected' : ''}`}
            style={{ background: colorToHex(c) }}
            title={colorToHex(c)}
            onClick={() => apply(c)}
          />
        ))}
      </div>

      <SavedColors current={current} onPick={apply} />
    </Panel>
  );
}

/**
 * The user's own colours, kept in named groups.
 *
 * Recent colours are a rolling window and lose things; a group is deliberate —
 * a project's palette stays put until it is deleted.
 */
function SavedColors({
  current,
  onPick,
}: {
  current: Color;
  onPick: (color: Color) => void;
}): JSX.Element {
  const [groups, setGroups] = useState<SavedPalette[]>(() => listPalettes());
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const refresh = useCallback(() => setGroups(listPalettes()), []);

  const create = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // A new group starts with the colour in hand, which is almost always why
    // it is being created.
    const made = createPalette(trimmed, current === TRANSPARENT ? [] : [current]);
    if (!made) {
      useEditor.getState().notify('Could not save — browser storage is unavailable.', 'error');
      return;
    }
    setName('');
    setNaming(false);
    refresh();
  };

  return (
    <>
      <div className="group-title" style={{ marginTop: 4 }}>
        My colours
        <span className="spacer" />
        <button className="icon-btn" title="New group" onClick={() => setNaming(true)}>
          <FolderPlus size={13} />
        </button>
      </div>

      {naming ? (
        <div className="row" style={{ marginBottom: 8 }}>
          <input
            className="input"
            autoFocus
            placeholder="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') create();
              if (e.key === 'Escape') setNaming(false);
              e.stopPropagation();
            }}
          />
          <button className="btn solid" onClick={create} disabled={!name.trim()}>
            Add
          </button>
          <button className="icon-btn" onClick={() => setNaming(false)} title="Cancel">
            <X size={13} />
          </button>
        </div>
      ) : null}

      {groups.length === 0 && !naming ? (
        <Hint>Make a group to keep one project&apos;s colours together.</Hint>
      ) : null}

      {groups.map((group) => (
        <div key={group.id} style={{ marginBottom: 10 }}>
          <div className="row" style={{ marginBottom: 4 }}>
            <span className="faint" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {group.name}
            </span>
            <button
              className="icon-btn"
              title={`Add the current colour to ${group.name}`}
              disabled={current === TRANSPARENT}
              onClick={() => {
                if (!addColorToPalette(group.id, current)) {
                  useEditor.getState().notify('That colour is already in the group.', 'warn');
                  return;
                }
                refresh();
              }}
            >
              <Plus size={13} />
            </button>
            <button
              className="icon-btn"
              title={`Delete ${group.name}`}
              onClick={() => {
                removePalette(group.id);
                refresh();
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
          {group.colors.length === 0 ? (
            <span className="faint" style={{ fontSize: 11 }}>
              Empty — press + to add the current colour.
            </span>
          ) : (
            <div className="swatch-grid">
              {group.colors.map((hex) => (
                <button
                  key={hex}
                  className={`swatch${hexToColor(hex) === current ? ' selected' : ''}`}
                  style={{ background: hex }}
                  title={`${hex} — right-click to remove`}
                  onClick={() => onPick(hexToColor(hex))}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    removeColorFromPalette(group.id, hex);
                    refresh();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
